import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Menu } from '@ovComponents/2-component/menu/menu';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import {
  EXPORT_PROGRESS_DECIMAL_PLACES,
  EXPORT_PROGRESS_PERCENT_SCALE,
} from '@ovComponents/3-pattern/exportPoll/constants';
import { formatPercentValue } from '@utils/moneyFormatting';
import { Box, IconButton, Tooltip, Typography, useTheme } from 'hephaestus/web';
import {
  Close as CloseIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
} from 'hephaestus/webIcons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadButtonConfigs } from '../hooks/useDownloadButtonConfigs';
import {
  buildExportAdditionalFilter,
  buildExportFilterAll,
  buildPortfolioExportFileName,
  getPortfolioExportColumns,
  PORTFOLIO_EXPORT_DATASET_PATH,
  PORTFOLIO_EXPORT_GQL,
  PORTFOLIO_EXPORT_QUERY_PATH,
  type PortfolioExportFilter,
} from '../utils/portfolioExportConfig';
export type PortfolioDownloadButtonProps = {
  organizationId: string;
  listFilter: PortfolioExportFilter;
  disabled?: boolean;
};

export const PortfolioDownloadButton = ({
  organizationId,
  listFilter,
  disabled,
}: PortfolioDownloadButtonProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const { t } = useTranslation(['portfolioTradingHub', 'shared']);
  const theme = useTheme();

  const columns = useMemo(() => getPortfolioExportColumns(t), [t]);
  const baseFilter = useMemo(
    () => buildExportFilterAll(organizationId),
    [organizationId],
  );
  const additionalFilter = useMemo(
    () => buildExportAdditionalFilter(listFilter),
    [listFilter],
  );

  const { onExport, activeExportId, dismissProgressDialog, progress } =
    useDownloadButtonConfigs({
      gql: PORTFOLIO_EXPORT_GQL,
      filter: baseFilter,
      additionalFilter,
      columns,
      fileName: buildPortfolioExportFileName(t),
      queryPath: PORTFOLIO_EXPORT_QUERY_PATH,
      datasetPath: PORTFOLIO_EXPORT_DATASET_PATH,
      sortField: 'updatedAt',
      sortDesc: true,
    });

  const closeMenu = () => setMenuAnchorEl(null);

  return (
    <>
      <Tooltip title={t('download.button')}>
        <span>
          <IconButton
            onClick={(event) => setMenuAnchorEl(event.currentTarget)}
            data-testid='portfolio-download-button'
            disabled={disabled}
            aria-label={t('download.button')}
          >
            <FileDownloadOutlinedIcon fontSize='inherit' />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            void onExport();
          }}
          data-testid='portfolio-download-all'
        >
          {t('download.all')}
        </MenuItem>
        <Tooltip title={t('download.filtersTooltip')} placement='left'>
          <span>
            <MenuItem
              onClick={() => {
                closeMenu();
                void onExport({
                  selectedColumns: columns,
                  useAdditionalFilter: true,
                });
              }}
              data-testid='portfolio-download-with-filters'
            >
              {t('download.withFilters')}
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      <Dialog
        onClose={dismissProgressDialog}
        open={activeExportId !== null}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {t('download.progressTitle')}
            <Tooltip title={String(t('shared:close'))}>
              <IconButton
                onClick={dismissProgressDialog}
                data-testid='portfolio-download-progress-dialog-close-button'
                sx={{ color: `${theme.color.onSurface}` }}
                aria-label={String(t('shared:close'))}
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                height: '8px',
                borderRadius: '4px',
                backgroundColor: `${theme.color.surfaceContainerVariant}`,
                width: '90%',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: `${theme.color.primary}`,
                  transition: 'width 0.5s',
                  width: `${progress * EXPORT_PROGRESS_PERCENT_SCALE}%`,
                }}
              />
            </Box>
            <Typography
              variant='bodySmall'
              component='p'
              sx={{ fontWeight: 500 }}
            >
              {formatPercentValue(progress, EXPORT_PROGRESS_DECIMAL_PLACES)}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
